import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import { ColumnDef } from "@tanstack/react-table";
import { CodeCell, IndexColumn } from "components/common/columns";
import { toast } from "react-toastify";
import { getDocumentTitle } from "utils/helpers";
import type { Supplier } from "../types/supplier.types";
import { deleteSupplier, getSuppliersList } from "../services/SupplierService";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const SupplierList = () => {
  document.title = getDocumentTitle("Suppliers");
  const { can } = useAccess();
  const canCreate = can(Permissions.SUPPLIER.CREATE);
  const canUpdate = can(Permissions.SUPPLIER.UPDATE);
  const canDelete = can(Permissions.SUPPLIER.DELETE);
  const tableRef = useRef<() => void>(null);
  const [target, setTarget] = useState<Supplier | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const onDelete = useCallback(async () => {
    if (!target) return;
    try {
      const res = await deleteSupplier(target.id);
      if (res.success) {
        toast.success(res.message);
        setDeleteOpen(false);
        tableRef.current?.();
      } else toast.error(res.message);
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Delete failed",
      );
    }
  }, [target]);

  const columns: ColumnDef<Supplier>[] = useMemo(
    () => [
      IndexColumn<Supplier>(),
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <Link to={`/inventory-management/suppliers/${row.original.id}`} className="text-decoration-none link">
            <CodeCell code={row.original.code ?? "—"} />
          </Link>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <Link
            to={`/inventory-management/suppliers/${row.original.id}`}
            className="text-body text-decoration-none"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        cell: ({ row }) => row.original.email ?? "—",
      },
      {
        header: "Phone",
        accessorKey: "phone",
        enableColumnFilter: false,
        cell: ({ row }) => row.original.phone ?? "—",
      },
      {
        header: "Active",
        id: "isActive",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.isActive === false ? (
            <span className="text-muted">No</span>
          ) : (
            <span className="text-success">Yes</span>
          ),
      },
      {
        header: "Actions",
        id: "actions",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Supplier actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                <DropdownItem
                  tag={Link}
                  to={`/inventory-management/suppliers/${r.id}`}
                >
                  <i className="ri-eye-line align-bottom me-2 text-muted" />
                  View
                </DropdownItem>
                {canUpdate ? (
                  <DropdownItem
                    tag={Link}
                    to={`/inventory-management/suppliers/edit/${r.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <DropdownItem
                    className="text-danger"
                    onClick={() => {
                      setTarget(r);
                      setDeleteOpen(true);
                    }}
                  >
                    <i className="ri-delete-bin-line align-bottom me-2" />
                    Delete
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canDelete, canUpdate],
  );

  return (
    <>
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={() => void onDelete()}
        onCloseClick={() => setDeleteOpen(false)}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Suppliers" pageTitle="Inventory Management" />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="card-title mb-0">Suppliers</h5>
              {canCreate ? (
                <Link
                  to="/inventory-management/suppliers/create"
                  className="btn btn-primary"
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add supplier
                </Link>
              ) : null}
            </CardHeader>
            <CardBody className="pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getSuppliersList}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default SupplierList;
